import { useRef, useState } from "react";
import { Observer } from "mobx-react";
import Link from "next/link";

import { Button, getTheme, MenuBar, MessageBox } from "components/UI";
import { getProp } from "utils/Utils";

import DiscountInput from "../DiscountInput/DiscountInput";
import CartPricing from "../CartPricing/Pricing";
import CartProduct from "../CartProduct/CartProduct";

import translationStore from "stores/translation-store";

import style from "./style"
import { faGift as emptyCartIcon } from "@fortawesome/pro-light-svg-icons/faGift";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useShop } from "stores/shop/shop";
import { validateDiscountWithUser, saveCartWithUser } from "stores/shop/utils";
import { useUser } from "utils/user/lib";
import { useApp } from "stores/app/context";
import { runInAction } from "mobx";

const showHolidayMessage = false;

export default function Cart(props)
{
  const user = useUser();
  const { toast } = useApp();
  const { cart } = useShop();

  const [discountLoader, setDiscountLoader] = useState(false);
  const [errorFields, setErrorFields] = useState([]);
  const discountRef = useRef(null);
  const saveCancel = useRef(null);

  function validateDiscount()
  {
    return validateDiscountWithUser(cart, user);
  }

  function saveCart()
  {
    if(saveCancel.current)
      saveCancel.current();

    return new Promise((resolve, reject) =>
    {
      saveCartWithUser(cart, user)
      .then(([promise, cancelFunc]) =>
      {
        saveCancel.current = cancelFunc;
        promise.then((cartData) =>
        {
          cart.hydrate(cartData);

          saveCancel.current = null;
          resolve();
        })
        .catch(reject);
      })
      .catch(reject);
    });
  }

  return (
    <Observer>
    {
      () => {
        return <>
          <MenuBar showCartCount={false}>
            <div key="left">
              <MenuBar.Back />
            </div>
            <div key="center">
              <MenuBar.Logo />
            </div>
            <div key="right">
            </div>
            <div key="mobile">
              <MenuBar.Back />
              <MenuBar.Logo />
            </div>
          </MenuBar>
          <div className="cart">
            {cart.products.length > 0 && (
              <div className="cart-top">
                {
                  showHolidayMessage && (
                    <div className="holiday-message max-width">
                      {translationStore.translate("shop.shipping.xmas-delay")}
                    </div>
                  )
                }
                {
                  props.message && props.message.key &&
                  <MessageBox
                    style={{marginBottom: 50, maxWidth: 350}}
                    background={getTheme().accentColorLightest}
                  >
                    {translationStore.translate(props.message.key, props.message.parameters)}
                    {props.message.parameters.discountCode!==undefined ? <span allow-context-menu={"true"} className="copy-paste">{props.message.parameters.discountCode}</span> : ''}
                  </MessageBox>
                }

                <div className="title max-width">
                  <span>{translationStore.translate("shop.page.cart.title")}</span>
                </div>
              </div>
            )}

            <div className={"cart-contents" + (cart.products.length>0 ? " filled" : " empty")}>
            {
              cart.products.length > 0 ?
              <>
                <div className="cart-items max-width">
                  {
                    cart.products.map(product =>
                      <CartProduct editable showLink key={product.key} currency={cart.currency} product={product} onChange={() => saveCart()} />
                    )
                  }
                </div>
                <div className="cart-bottom max-width">
                  <div style={{marginBottom: 30}}>
                    <DiscountInput
                      ref={discountRef}
                      loading={discountLoader}
                      value={cart.discount.code}
                      errorFields={getProp(errorFields, ['discount'])}
                      onSave={code =>
                      {
                        setDiscountLoader(true);
                        runInAction(() => {
                          cart.discount.code = code;
                        });

                        validateDiscount()
                        .then(() =>
                        {
                          saveCart()
                          .then(() =>
                          {
                            setDiscountLoader(false);
                          })
                          .catch(() =>
                          {
                            runInAction(() => {
                              cart.discount.code = "";
                            });

                            setDiscountLoader(false);
                            toast.add({
                              message:  translationStore.translate("base.error"),
                              className: "error"
                            });
                          })
                        })
                        .catch(errorFields =>
                        {
                          setTimeout(() =>
                          {
                            discountRef.current.focus();
                          }, 100);

                          runInAction(() => {
                            cart.discount.code = "";
                          });

                          setDiscountLoader(false);
                          setErrorFields(errorFields);
                        });
                      }}
                    />
                  </div>
                  <div>
                    <CartPricing language={translationStore.getLanguage()} currency={cart.currency} pricing={cart.getPricing()} />
                  </div>
                </div>
                <div className="cart-bottom-button max-width">
                  <Link href={"/myalbums"} passHref legacyBehavior>
                    <Button>{translationStore.translate("shop.page.cart.to-your-albums")}</Button>
                  </Link>
                  <Link href={"/shop/checkout"} passHref legacyBehavior>
                    <Button className="accent large">{translationStore.translate("shop.page.cart.continue")}</Button>
                  </Link>
                </div>
              </> :
              <>
                <div className="cart-empty max-width">
                  <FontAwesomeIcon icon={emptyCartIcon} color={"rgba(var(--accentColorLight), 1)"} />

                  <div className="title">{translationStore.translate("shop.page.cart.empty.title")}</div>
                  {
                    user.isLoggedIn ?
                    <>
                      <div className="description">{translationStore.translate("shop.page.cart.empty.description")}</div>
                      <Link href="/myalbums" passHref legacyBehavior>
                        <Button className="filled">{translationStore.translate("shop.page.cart.empty.logged-in.button")}</Button>
                      </Link>
                    </>
                    :
                    <>
                      <div className="description">{translationStore.translate("shop.page.cart.empty.description")}</div>
                      <Link href="/myalbums" passHref legacyBehavior>
                        <Button className="filled">{translationStore.translate("shop.page.cart.empty.logged-out.button")}</Button>
                      </Link>
                    </>
                  }
                </div>
              </>
            }
            </div>
          </div>

          <style jsx>{style}</style>
          <style jsx>{`
          .cart-notification {
            width: 100%;
            padding: 20px;
            border-radius: 6px;
            text-align: center;
            background-color: rgba(var(--notificationColor), 1);
            margin-bottom: 50px;
          }
          `}</style>
        </>;
      }
    }
    </Observer>
  );
}